import React, { useRef } from 'react'
import { Tooltip } from '@contrast-security-inc/design-system-popovers'
import { css, cx } from '@emotion/css'
import { Placement } from '@popperjs/core'

export const defaultTooltipPadding = 15

export interface WithTooltipChildrenFnProps {
  readonly ref?: React.Ref<any>
  readonly isOpen?: boolean
  readonly isTransitioning?: boolean
  readonly onMouseEnter?: () => void
  readonly onMouseLeave?: () => void
}

export interface WithTooltipProps {
  readonly renderTooltipContent: () => any
  readonly children: (props: WithTooltipChildrenFnProps) => React.ReactNode
  readonly disabled?: boolean
  readonly placement?: Placement
  readonly positionFixed?: boolean
  readonly width?: string
  readonly delayRender?: boolean
  readonly tiny?: boolean
  readonly containerClassName?: string
  readonly popperContainerClassname?: string
  readonly tooltipMaxWidth?: string
  readonly dataTestId?: string
  readonly fontSize?: number
}

type TooltipElement = HTMLElement | null

const containerWidthStyles = (
  width: WithTooltipProps['width'],
  tooltipMaxWidth: WithTooltipProps['tooltipMaxWidth'],
  fontSize: WithTooltipProps['fontSize']
) => css`
  display: flex;
  cursor: default;
  ${width ? `width: ${width}` : undefined};
  ${tooltipMaxWidth ? `max-width: ${tooltipMaxWidth}` : undefined};
  ${fontSize ? `font-size: ${fontSize}px` : undefined};
`

export const WithTooltip = ({
  renderTooltipContent,
  children,
  placement = 'auto',
  disabled = false,
  positionFixed = false,
  width,
  tiny = false,
  containerClassName,
  popperContainerClassname,
  tooltipMaxWidth,
  delayRender = true,
  dataTestId,
  fontSize
}: WithTooltipProps) => {
  const referenceElement = useRef<TooltipElement>(null)

  return (
    <Tooltip
      interactive
      openDelay={300}
      closeDelay={300}
      enabled={!disabled}
      testId={dataTestId}
      fixed={positionFixed}
      placement={placement}
      ref={referenceElement}
      isDelayed={delayRender}
      contentClassName={popperContainerClassname}
      size={tiny ? 'sm' : 'md'}
      body={renderTooltipContent()}
      className={cx(
        containerClassName,
        containerWidthStyles(width, tooltipMaxWidth, fontSize)
      )}
    >
      <div>
        {children({
          ref: referenceElement
        })}
      </div>
    </Tooltip>
  )
}
